import type { ApiClient } from '@center-technologies/shared-api-clients';
import type { PermissionList } from 'types/permissions';

export interface FetchPermissionListParams {
  resourceType: string;
  resourceId: string;
  permissions?: string[];
}

interface BatchPermissionsResponse {
  permissions: Record<string, Record<string, boolean>>;
}

/**
 * Checks multiple permissions for the authenticated user on a sphere,
 * routing through the batch `POST /spheres/permissions` endpoint.
 *
 * The `is_private` caveat is applied server-side — parity with `can-do`.
 * `resourceType` is kept for call-site compatibility but is not sent to
 * the endpoint (which derives sphere identity from `resourceId` alone).
 */
export async function fetchPermissionList(
  apiClient: ApiClient,
  params: FetchPermissionListParams,
): Promise<PermissionList> {
  const { resourceId, permissions = [] } = params;

  if (!resourceId) {
    throw new Error('Invalid parameters: resourceId is required.');
  }

  const response = await (
    apiClient as unknown as {
      post: <T>(path: string, data: unknown) => Promise<T>;
    }
  ).post<BatchPermissionsResponse>('/spheres/permissions', {
    sphereIds: [resourceId],
    actions: permissions,
  });

  const spherePermissions = response?.permissions?.[resourceId] ?? {};
  const result: Record<string, boolean> = {};
  for (const action of permissions) {
    result[action] = spherePermissions[action] ?? false;
  }

  return { permissions: result };
}
